import { Box, Grid } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { Dataset } from "@nens/rana/api-v1";
import { RanaTable } from "@nens/ranalib";
import { RanaLoading } from "@nens/ranalib";
import { useDebounce } from "@nens/react/hooks";

import { DatasetQueryParams, useDatasetListQuery } from "@/queries/query-dataset";

import { DATASET_CARD_HEIGHT, DatasetCard } from "./dataset-card";

const TRANSITION_TIME = 200;
const TRANSITION = `all ${TRANSITION_TIME}ms`;

interface DatasetListProps {
  category: string | undefined;
  setDataset: (dataset: Dataset | undefined) => void;
  spatialRepresentationType?: string;
  searchString: string | undefined;
  page: number;
  offset: number;
  limit: number;
  setPageSize: (size: number) => void;
  setPage: (page: number) => void;
}

export const DatasetList = ({
  category: nonDelayedCategory,
  searchString,
  ...rest
}: DatasetListProps) => {
  const delayedCategory = useDebounce(nonDelayedCategory, TRANSITION_TIME);
  const category = nonDelayedCategory ?? delayedCategory;

  if (category || searchString)
    return <DatasetListInner category={category} searchString={searchString} {...rest} />;

  return null;
};

const DatasetListInner = ({
  category,
  spatialRepresentationType,
  searchString,
  setDataset,
  page,
  offset,
  limit,
  setPageSize,
  setPage,
}: DatasetListProps) => {
  if (!category && !searchString) console.warn("Either category or search should be defined");

  const debouncedSearchString = useDebounce(searchString, 500);

  const params: DatasetQueryParams = {
    category,
    limit,
    offset,
    search: debouncedSearchString || undefined,
  };
  if (spatialRepresentationType) {
    params.clSpatialRepresentationTypeKey = [spatialRepresentationType];
  }

  const { data: datasetList, isLoading } = useDatasetListQuery(params);

  const numPages = Math.ceil((datasetList?.total ?? 0) / limit);

  return (
    <>
      <RanaLoading
        transition={TRANSITION}
        bg="brand.grey.200"
        top={0}
        left={0}
        w="full"
        h={DATASET_CARD_HEIGHT}
        position="absolute"
        alignItems="center"
        isCentered
        opacity={isLoading ? 1 : 0}
        pointerEvents={isLoading ? "all" : "none"}
        zIndex={1}
      />

      <Box flexGrow={1} position="relative">
        <Box position="absolute" w="full" h="full" top={0} left={0}>
          <Grid
            templateColumns={{
              base: "repeat(1, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
            }}
            flexWrap="wrap"
            gap={4}
          >
            {(() => {
              if (isLoading) return null;

              const items = datasetList?.items ?? [];

              if (items.length === 0) {
                return (
                  <FormattedMessage
                    id="message.no_datasets_with_search"
                    defaultMessage="No datasets with current search term"
                  />
                );
              }

              return items.map((dataset) => (
                <DatasetCard key={dataset.id} setDataset={setDataset} dataset={dataset} />
              ));
            })()}
          </Grid>
          {numPages > 1 && (
            <RanaTable.Pagination
              limit={limit}
              setPageSize={setPageSize}
              page={page}
              numPages={numPages}
              setPage={setPage}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
